import Microgame from '../engine/Microgame.js';

export default class ShaveGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Rase !`, en: `Shave!`, es: `¡Afeita!` },
            controls: { fr: `Glisse le rasoir de haut en bas sur les poils`, en: `Swipe the razor down over the hairs`, es: `Desliza la maquinilla de arriba abajo sobre los pelos` },
            objective: { fr: `Rase tous les poils de la zone sélectionnée`, en: `Shave all the hairs in the selected area`, es: `Afeita todos los pelos de la zona seleccionada` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M6 10h12"/><path d="M8 10V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4"/><path d="M9 22V10"/><path d="M15 22V10"/></svg>`,
            actions: [
                { type: 'swipe', fromX: 42, fromY: 52, toX: 42, toY: 76, duration: 220 },
                { type: 'swipe', fromX: 50, fromY: 52, toX: 50, toY: 76, duration: 220 },
                { type: 'swipe', fromX: 58, fromY: 52, toX: 58, toY: 76, duration: 220 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.canvas = null;
        this.ctx = null;
        this.isShaving = false;
        this.face = null;
        this.initialBeardPixels = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "SHAVE!";

        // Face (Primary Color)
        this.face = document.createElement('div');
        this.face.style.position = 'absolute';
        this.face.style.width = '200px';
        this.face.style.height = '250px';
        this.face.style.background = this.primaryColor;
        this.face.style.borderRadius = '60px';
        this.face.style.left = '50%';
        this.face.style.top = '50%';
        this.face.style.transform = 'translate(-50%, -50%)';
        this.face.style.boxShadow = `0 0 20px ${this.primaryColor}`;

        // Eyes
        const eyeL = document.createElement('div');
        eyeL.style.position = 'absolute';
        eyeL.style.width = '20px';
        eyeL.style.height = '20px';
        eyeL.style.background = '#000'; // Keep eyes black for contrast
        eyeL.style.borderRadius = '50%';
        eyeL.style.top = '80px';
        eyeL.style.left = '50px';
        this.face.appendChild(eyeL);

        const eyeR = document.createElement('div');
        eyeR.style.position = 'absolute';
        eyeR.style.width = '20px';
        eyeR.style.height = '20px';
        eyeR.style.background = '#000';
        eyeR.style.borderRadius = '50%';
        eyeR.style.top = '80px';
        eyeR.style.right = '50px';
        this.face.appendChild(eyeR);

        // Mouth (Smile) - Initially hidden or small? Let's make it a straight line first
        const mouth = document.createElement('div');
        mouth.style.position = 'absolute';
        mouth.style.width = '60px';
        mouth.style.height = '10px';
        mouth.style.background = '#000';
        mouth.style.borderRadius = '5px';
        mouth.style.bottom = '70px';
        mouth.style.left = '50%';
        mouth.style.transform = 'translateX(-50%)';
        this.face.appendChild(mouth);

        this.container.appendChild(this.face);

        // Beard Canvas (Secondary Color)
        this.canvas = document.createElement('canvas');
        this.canvas.width = this.container.clientWidth;
        this.canvas.height = this.container.clientHeight;
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.container.appendChild(this.canvas);

        this.ctx = this.canvas.getContext('2d');
        this.drawBeard();

        // Count initial pixels
        this.initialBeardPixels = this.countBeardPixels();

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    drawBeard() {
        const faceRect = this.face.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();

        const x = faceRect.left - containerRect.left;
        const y = faceRect.top - containerRect.top;
        const w = faceRect.width;
        const h = faceRect.height;

        this.ctx.fillStyle = this.secondaryColor;

        // Beard shape
        this.ctx.beginPath();
        this.ctx.moveTo(x, y + h / 2);
        this.ctx.lineTo(x + w, y + h / 2);
        this.ctx.lineTo(x + w, y + h - 20);
        this.ctx.quadraticCurveTo(x + w / 2, y + h + 20, x, y + h - 20);
        this.ctx.fill();

        // Mustache
        this.ctx.beginPath();
        this.ctx.rect(x + 50, y + h / 2 - 20, w - 100, 30);
        this.ctx.fill();
    }

    countBeardPixels() {
        const faceRect = this.face.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();

        // Scan only the beard area
        const startX = Math.floor(faceRect.left - containerRect.left);
        const startY = Math.floor(faceRect.top - containerRect.top + faceRect.height / 2 - 30);
        const endX = startX + faceRect.width;
        const endY = startY + faceRect.height / 2 + 60;

        // Safety check
        if (startX < 0 || startY < 0 || endX > this.canvas.width || endY > this.canvas.height) return 1000; // Fallback

        const imgData = this.ctx.getImageData(startX, startY, endX - startX, endY - startY).data;
        let count = 0;
        for (let i = 3; i < imgData.length; i += 4 * 4) { // Stride 4 for speed
            if (imgData[i] > 0) count++;
        }
        return count;
    }

    onDown(e) {
        if (!this.active) return;
        this.isShaving = true;
        this.shave(e);
    }

    onMove(e) {
        if (!this.active || !this.isShaving) return;
        this.shave(e);
    }

    onUp() {
        this.isShaving = false;
        this.checkWin();
    }

    shave(e) {
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        this.ctx.globalCompositeOperation = 'destination-out';
        this.ctx.beginPath();
        this.ctx.arc(x, y, 25, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.globalCompositeOperation = 'source-over';

        // Check win every few frames or just every move (it's fast enough usually)
        this.checkWin();
    }

    checkWin() {
        if (this.won) return;

        const currentPixels = this.countBeardPixels();

        // Safety check for initial count
        if (this.initialBeardPixels === 0) {
            this.initialBeardPixels = 1; // Prevent divide by zero
        }

        const percentageLeft = currentPixels / this.initialBeardPixels;

        if (percentageLeft < 0.1) { // Less than 10% left
            this.win();
            this.canvas.style.opacity = '0'; // Clear rest

            // Change mouth to smile
            const mouth = this.face.lastChild;
            mouth.style.height = '30px';
            mouth.style.borderBottom = '5px solid #000';
            mouth.style.background = 'transparent';
            mouth.style.borderRadius = '0 0 30px 30px';
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
